import { isEmpty } from '@newfront-insurance/core';
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { AnimatePresence, motion } from 'framer-motion';
import { FileText, X, Loader2, AlertCircle } from 'lucide-react';
import { useCallback, type Dispatch, type SetStateAction } from 'react';

import type { FileAttachment } from '../../hooks/use-file-selection';

export function InputAttachmentsPreview({
  attachments,
  setAttachments,
  className,
}: {
  attachments: Array<FileAttachment>;
  setAttachments: Dispatch<SetStateAction<Array<FileAttachment>>>;
  className?: string;
}) {
  const handleRemoveAttachment = useCallback(
    (tempId: string) => {
      const attachmentToRemove = attachments.find((a) => a.tempId === tempId);
      if (attachmentToRemove) {
        browserLogger.info('[Nova] Removing attachment', {
          fileName: attachmentToRemove.file.name,
          tempId,
          wasUploading: attachmentToRemove.isUploading,
          hadError: !!attachmentToRemove.uploadError,
          fileUuid: attachmentToRemove.fileUuid,
        });
      }
      setAttachments((prev) => prev.filter((attachment) => attachment.tempId !== tempId));
    },
    [attachments, setAttachments],
  );

  if (isEmpty(attachments)) {
    return null;
  }

  return (
    <div className={cn('bg-steel-50 flex w-full flex-row gap-2 p-3', className)}>
      <AnimatePresence>
        {attachments.map((attachment) => (
          <motion.div
            key={attachment.tempId}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
              duration: 0.2,
              ease: 'easeInOut',
            }}
            className={cn(
              'bg-background flex items-center justify-between rounded-md border',
              'size-[70px] overflow-hidden shadow-xs transition-colors',
              'hover:border-rebrand-400 hover:bg-muted/50 relative',
              {
                'border-yellow-400': attachment.isUploading,
                'border-red-400': attachment.uploadError,
                'border-green-400': attachment.fileUuid && !attachment.isUploading,
              },
            )}
          >
            <FilePreview attachment={attachment} />

            {/* Upload status indicator */}
            {attachment.isUploading && (
              <div className="bg-background/80 absolute inset-0 flex items-center justify-center">
                <Loader2 className="size-4 animate-spin text-yellow-600" />
              </div>
            )}

            {attachment.uploadError && (
              <div className="bg-background/80 absolute inset-0 flex items-center justify-center">
                <AlertCircle className="size-4 text-red-600" />
              </div>
            )}

            <Button
              variant="transparent"
              size="small"
              className={cn(
                'bg-steel-100/75 absolute top-0.5 right-0.5 shrink-0 rounded-full',
                'hover:bg-rebrand-400 h-fit w-fit p-0.5 hover:text-white',
              )}
              onClick={() => handleRemoveAttachment(attachment.tempId)}
              type="button"
              disabled={attachment.isUploading}
            >
              <X className="size-2.5" />
            </Button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function getFileType(contentType: string) {
  if (contentType.startsWith('image/')) {
    return 'image';
  }
  if (contentType === 'application/pdf') {
    return 'pdf';
  }

  return 'file';
}

function FilePreview({ attachment }: { attachment: FileAttachment }) {
  const contentType = attachment.file.type || '';

  let inner = (
    <div className="flex h-full w-full flex-col items-center justify-center rounded-sm bg-gray-50">
      <FileText className="h-6 w-6 text-gray-600" />
      <span className="mt-1 text-xs font-medium text-gray-600">{getFileType(contentType).toUpperCase()}</span>
    </div>
  );
  if (contentType.startsWith('image/')) {
    inner = (
      <img
        src={URL.createObjectURL(attachment.file)}
        alt={attachment.file.name || 'Attachment preview'}
        className="h-full w-full rounded-sm object-cover"
      />
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger type="button" className="size-full cursor-help">
          {inner}
        </TooltipTrigger>
        <TooltipContent>
          {attachment.file.name}
          {attachment.isUploading && ' (uploading...)'}
          {attachment.uploadError && ' (upload failed)'}
          {attachment.fileUuid && !attachment.isUploading && ' (uploaded)'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
